'use client'

import type { QueryClient } from '@tanstack/react-query'
import { Toast } from '@skop-app/lib-front'

import { availableLocales } from '@/i18n/routing'
import {
  AUTH_REDIRECT_QUERY_PARAM,
  sanitizeRedirectPath,
} from '@/services/auth/redirectPath'
import { clearPostAuthRedirectPath } from '@/services/auth/clientAuthState'
import { reconcilePendingOrganizationInvitesForAuthenticatedUser } from '@/services/auth/postAuthInviteReconciliation.client'
import { performLocalSessionCleanup } from '@/services/auth/logoutCleanup.client'
import { waitForAuthenticatedUserSession } from '@/services/auth/session.client'
import { userKeys } from '@/services/user/user.keys'
import { devLog } from '@/utils/dev.utility'

type RouterLike = {
  replace: (href: string) => void
}

type ToastLike = Pick<typeof Toast, 'classic' | 'error'>

const supportedLocales = new Set(availableLocales.map(({ code }) => code))

function triggerPostAuthInviteReconciliationFallback(
  queryClient?: QueryClient,
): void {
  void reconcilePendingOrganizationInvitesForAuthenticatedUser()
    .then(() =>
      queryClient?.invalidateQueries({
        queryKey: userKeys.current(),
      }),
    )
    .catch((error) => {
      devLog('Unexpected error during post-auth invite reconciliation', error)
    })
}

function getLocalizedLoginPath(): string {
  if (typeof window === 'undefined') return '/login'

  const segments = window.location.pathname.split('/').filter(Boolean)
  const maybeLocale = segments[0]

  return maybeLocale && supportedLocales.has(maybeLocale)
    ? `/${maybeLocale}/login`
    : '/login'
}

function getNavigationTargetPath(redirectPath: string): string {
  const safeRedirectPath = sanitizeRedirectPath(redirectPath, '/')
  const suffixStart = safeRedirectPath.search(/[?#]/)
  const rawPath =
    suffixStart === -1
      ? safeRedirectPath
      : safeRedirectPath.slice(0, suffixStart)
  const rawSuffix =
    suffixStart === -1 ? '' : safeRedirectPath.slice(suffixStart)
  const segments = rawPath.split('/').filter(Boolean)
  const maybeLocale = segments[0]

  // next-intl navigation helpers expect internal app paths without a locale prefix.
  if (!maybeLocale || !supportedLocales.has(maybeLocale)) {
    return safeRedirectPath
  }

  const pathWithoutLocale =
    segments.length === 1 ? '/' : `/${segments.slice(1).join('/')}`

  return `${pathWithoutLocale}${rawSuffix}`
}

export function buildLocalizedPublicLoginHref(redirectPath: string): string {
  const loginPath = getLocalizedLoginPath()
  if (redirectPath === '/') {
    return loginPath
  }

  const nextParams = new URLSearchParams()
  nextParams.set(AUTH_REDIRECT_QUERY_PARAM, redirectPath)
  return `${loginPath}?${nextParams.toString()}`
}

type CompleteAuthNavigationParams = {
  router: RouterLike
  safeRedirectPath: string
  toast?: ToastLike
  successMessage?: string
  onBeforeRedirect?: () => void
  queryClient?: QueryClient
}

export async function clearPublicAuthLocalState(
  queryClient: QueryClient,
): Promise<void> {
  await performLocalSessionCleanup(queryClient, {
    skipSignOut: true,
    clearAppCookies: false,
    clearLegacyAuthState: true,
  })
}

export async function completeAuthNavigation({
  router,
  safeRedirectPath,
  toast,
  successMessage,
  onBeforeRedirect,
  queryClient,
}: CompleteAuthNavigationParams): Promise<boolean> {
  const sessionReady = await waitForAuthenticatedUserSession()
  if (!sessionReady) return false

  triggerPostAuthInviteReconciliationFallback(queryClient)
  clearPostAuthRedirectPath()
  if (toast && successMessage) {
    toast.classic(successMessage, { duration: 1000 })
  }
  onBeforeRedirect?.()
  router.replace(getNavigationTargetPath(safeRedirectPath))
  return true
}
